import type { Message as MessageType } from 'app/types/chat';
import { MessageKind } from 'app/types/chat';

// Resolve from the full conversation before UI filtering; deleted parents remain tombstones.
export function resolveMessageReplies(messages: MessageType[]): MessageType[] {
  const byId = new Map(messages.map((message) => [message.message_id, message]));
  return messages.map((message) => {
    if (!message.reply_to) return message;
    const parent = byId.get(message.reply_to.message_id);
    if (!parent) {
      // Older APIs can still supply a preview. A filtered/missing v16 parent has no preview.
      return message;
    }
    return {
      ...message,
      reply_to: {
        message_id: parent.message_id,
        type: parent.type,
        user: parent.user,
        is_deleted: parent.is_deleted,
        ...(!parent.is_deleted &&
          (parent.type === MessageKind.File ? { file: parent.file } : { message: parent.message })),
      },
    };
  });
}

export function mergeOptimisticReactions(
  messages: MessageType[],
  optimisticReactions: Map<string, string[]>,
  currentUserId: string | undefined,
) {
  if (optimisticReactions.size === 0) return messages;
  return messages.map((message) => {
    const optimistic = optimisticReactions.get(message.message_id);
    if (!optimistic?.length) return message;
    let merged = [...(message.reactions ?? [])];
    for (const emoji of optimistic) {
      const existingIndex = merged.findIndex((reaction) => reaction.emoji === emoji);
      if (existingIndex < 0) {
        if (currentUserId) merged.push({ emoji, userIds: [currentUserId] });
        continue;
      }
      const reaction = merged[existingIndex];
      if (!reaction) continue;
      const reacted = currentUserId ? reaction.userIds.includes(currentUserId) : false;
      const userIds =
        currentUserId && reacted
          ? reaction.userIds.filter((id) => id !== currentUserId)
          : currentUserId
            ? [...reaction.userIds, currentUserId]
            : reaction.userIds;
      const count = userIds.length;
      if (count === 0) merged = merged.filter((_, index) => index !== existingIndex);
      else merged[existingIndex] = { ...reaction, userIds };
    }
    return { ...message, reactions: merged.length > 0 ? merged : undefined };
  });
}
